import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import { apiClient } from 'Helpers/network';

import useApiRequest from 'Hooks/useApiRequest';
import { useToast } from 'Hooks/useToast';

/**
 * RADIO report builder context (IB-678).
 *
 * Mirrors the Project Plan report context: it holds the criteria, auto-fetches
 * the on-screen JSON whenever the criteria change, and drives the generate →
 * poll → download flow for the async Excel export.
 *
 * Contract (set by the Blade builder page):
 *   fetchReportUrl     GET  — on-screen data; criteria sent under criteria[...]
 *                            → { data: { rows, styles, summary } }
 *   exportReportUrl    POST — queues the Excel export (criteria at top level)
 *                            → { success, report: { id, ... } }
 *   downloadReportUrl  GET  — contains REPORT_CENTRE_EXPORT_ID placeholder
 *   checkStatusUrl     GET  — contains REPORT_CENTRE_EXPORT_ID placeholder
 *   options                 — every option list the criteria form needs
 *   channelName             — private Echo channel broadcasting `report.generated`
 */

const DEFAULT_CRITERIA = {
    item_types: [],
    scope_level: 'central',
    scope_entity_ids: [],
    include_central: false,
    statuses: [],
    rag_basis: 'gross',
    rag_classifications: [],
    priorities: [],
    impact_category_ids: [],
    risk_type_ids: [],
    owner_ids: [],
    afc_only: false,
    verified: null,
    action_status: 'all',
    group_by: 'type',
    detail_level: 'detailed',
    visible_columns: null,
    sort: null,
    format: 'register',
};

// How often we poll the export status endpoint while an export is generating.
const POLL_INTERVAL_MS = 4000;

const RadioReportContext = createContext();

export const useRadioReportContext = () => {
    const context = useContext(RadioReportContext);

    if (!context) {
        throw new Error('useRadioReportContext must be used within a RadioReportProvider');
    }

    return context;
};

export const RadioReportProvider = ({
    fetchReportUrl,
    exportReportUrl,
    downloadReportUrl,
    checkStatusUrl,
    options: rawOptions,
    channelName,
    initialCriteria,
    children,
}) => {
    const [criteria, setCriteria] = useState({ ...DEFAULT_CRITERIA, ...(initialCriteria || {}) });
    const [exporting, setExporting] = useState(false);

    const toast = useToast();

    const reportRequest = useApiRequest({ url: fetchReportUrl });

    const pollTimer = useRef(null);
    // Guards against the interval poll and the Echo listener both completing
    // simultaneously and each calling startDownload → window.open.
    const hasDownloaded = useRef(false);

    const options = useMemo(() => {
        const source = rawOptions || {};

        return {
            itemType: source.itemTypes || [],
            scope: source.scope || [],
            // Entity options keyed by scope level ({ department, costcentre, venue }).
            scopeEntitiesByLevel: source.scopeEntitiesByLevel || {},
            status: source.statuses || [],
            impactCategory: source.impactCategories || [],
            riskType: source.riskTypes || [],
            owner: source.owners || [],
            ragClassification: source.ragClassifications || [],
            priority: source.priorities || [],
            groupBy: source.groupBy || [],
            column: source.columns || [],
        };
    }, [rawOptions]);

    const fetchReport = useCallback(
        (nextCriteria) => {
            const { reset, get } = reportRequest;

            reset();

            // Nest the criteria so the backend reads them from input('criteria').
            return get({ criteria: nextCriteria });
        },
        [reportRequest.reset, reportRequest.get],
    );

    // Re-fetch whenever the criteria change (the first render fetches too).
    useEffect(() => {
        fetchReport(criteria);
    }, [criteria]);

    const updateCriteria = useCallback((partial) => {
        setCriteria((prev) => ({ ...prev, ...partial }));
    }, []);

    const clearPolling = useCallback(() => {
        if (pollTimer.current) {
            clearInterval(pollTimer.current);
            pollTimer.current = null;
        }
    }, []);

    const startDownload = useCallback(
        (reportId) => {
            if (hasDownloaded.current) {
                return;
            }
            hasDownloaded.current = true;
            const url = downloadReportUrl.replace('REPORT_CENTRE_EXPORT_ID', reportId);
            window.open(url, '_blank');
        },
        [downloadReportUrl],
    );

    const pollStatus = useCallback(
        (reportId) => {
            const url = checkStatusUrl.replace('REPORT_CENTRE_EXPORT_ID', reportId);

            return apiClient
                .get(url)
                .then((response) => {
                    const statusKey = response.data?.item?.statusKey;

                    if (statusKey === 'completed') {
                        clearPolling();
                        setExporting(false);
                        startDownload(reportId);
                        toast.success($t('Your report is ready and the download has started.'));
                        return true;
                    }

                    if (statusKey === 'failed') {
                        clearPolling();
                        setExporting(false);
                        toast.error($t('Failed to generate the report. Please try again later.'));
                        return true;
                    }

                    return false;
                })
                .catch(() => false);
        },
        [checkStatusUrl, clearPolling, startDownload, toast],
    );

    const exportReport = useCallback(() => {
        if (exporting) {
            return Promise.resolve();
        }

        setExporting(true);
        hasDownloaded.current = false;

        return apiClient
            .post(exportReportUrl, criteria)
            .then((response) => {
                const reportId = response.data?.report?.id;

                if (!reportId) {
                    throw new Error('Missing export report id');
                }

                toast.success($t('The request to generate a report is being processed. This may take a few minutes'));

                clearPolling();
                pollTimer.current = setInterval(() => pollStatus(reportId), POLL_INTERVAL_MS);

                return reportId;
            })
            .catch((error) => {
                setExporting(false);
                toast.error($t('Failed to generate the report. Please try again later.'));
                throw error;
            });
    }, [exporting, exportReportUrl, criteria, toast, clearPolling, pollStatus]);

    // Listen for the broadcast so a completed export downloads even if the poll
    // timer is slow (parity with the Project Plan report).
    useEffect(() => {
        if (!channelName || !window.Echo) {
            return undefined;
        }

        const channel = window.Echo.private(channelName);

        channel.listen('.report.generated', (event) => {
            const reportId = event?.report?.id;

            if (reportId) {
                pollStatus(reportId);
            }
        });

        return () => {
            window.Echo.leave(channelName);
        };
    }, [channelName, pollStatus]);

    // Tidy the poll timer on unmount.
    useEffect(() => clearPolling, [clearPolling]);

    const value = {
        criteria,
        options,
        updateCriteria,
        exportReport,
        exporting,
        report: reportRequest.data?.data ?? null,
        loading: reportRequest.loading,
    };

    return <RadioReportContext.Provider value={value}>{children}</RadioReportContext.Provider>;
};

export default RadioReportContext;
